import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import {
  createProntuario,
  getApiErrorMessage,
  getProntuarioByColaborador,
  getProntuarioId,
  Prontuario,
  ProntuarioApiData,
} from "src/apis/prontuario";
import { Id } from "src/utils/types";
import SkeletonLoad from "../SkeletonLoad";
import ClinicalHistory from "./ClinicalHistory";
import ClinicalNotesList from "./ClinicalNotesList";
import ProntuarioGeneralForm from "./ProntuarioGeneralForm";
import ProntuarioMedications from "./ProntuarioMedications";
import {
  formatEmpty,
  getStatusColor,
  prontuarioStatusLabels,
  riskLabels,
} from "./utils";

type ProntuarioDrawerProps = {
  idColaborador: Id;
  open: boolean;
  onClose: () => void;
  presentation?: "drawer" | "dialog";
};

type ToastState = {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

type TabPanelProps = {
  activeTab: number;
  index: number;
  children: ReactNode;
};

const TabPanel: FC<TabPanelProps> = ({ activeTab, index, children }) => {
  return activeTab === index ? (
    <Box sx={{ pt: 2 }}>
      {children}
    </Box>
  ) : null;
};

const ProntuarioDrawer: FC<ProntuarioDrawerProps> = ({
  idColaborador,
  open,
  onClose,
  presentation = "dialog",
}) => {
  const [apiData, setApiData] = useState<ProntuarioApiData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = useCallback(
    (message: string, severity: ToastState["severity"] = "success") => {
      setToast({ open: true, message, severity });
    },
    []
  );

  const fetchProntuario = useCallback(async () => {
    if (!open || !idColaborador) return;

    setLoading(true);
    setError("");
    setApiData(null);

    try {
      const response = await getProntuarioByColaborador(idColaborador);
      setApiData(response);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Não foi possível carregar o prontuário."));
    } finally {
      setLoading(false);
    }
  }, [idColaborador, open]);

  useEffect(() => {
    fetchProntuario();
  }, [fetchProntuario]);

  const handleCreateProntuario = async () => {
    if (!idColaborador) {
      showToast("Não foi possível identificar o paciente.", "error");
      return;
    }

    setCreating(true);
    setError("");

    try {
      const response = await createProntuario(idColaborador);
      setApiData(response);
      setActiveTab(0);
      showToast("Prontuário criado com sucesso.", "success");
    } catch (err: unknown) {
      showToast(
        getApiErrorMessage(err, "Não foi possível criar o prontuário."),
        "error"
      );
    } finally {
      setCreating(false);
    }
  };

  const prontuario = apiData?.prontuario ?? null;
  const prontuarioId = getProntuarioId(prontuario);
  const notFound = apiData !== null && !apiData.possui_prontuario;
  const patientName = formatEmpty(apiData?.colaborador?.nome ?? prontuario?.colaborador?.nome);
  const companyName = formatEmpty(
    apiData?.empresa?.trade_name ??
      apiData?.empresa?.social_reason ??
      apiData?.empresa?.nome
  );
  const risk = String(prontuario?.nivel_risco ?? "nao_informado");
  const status = String(prontuario?.status ?? "ativo");
  const isDialog = presentation === "dialog";

  const content = (
    <Box
      sx={{
        width: isDialog
          ? "100%"
          : { xs: "100vw", sm: 760, md: 940 },
        maxWidth: "100vw",
        p: isDialog ? 0 : 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Prontuário clínico
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {patientName !== "-" ? patientName : `Paciente ${formatEmpty(idColaborador)}`}
          </Typography>
        </Box>
        <Tooltip title="Fechar">
          <IconButton onClick={onClose} sx={isDialog ? { mt: -1, mr: -1 } : undefined}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      {loading && <SkeletonLoad variant="form" rows={8} />}

      {Boolean(error) && !loading && (
        <Alert severity="error">{error}</Alert>
      )}

      {notFound && !loading && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              startIcon={creating ? undefined : <AddIcon />}
              disabled={creating}
              onClick={handleCreateProntuario}
            >
              {creating ? "Criando..." : "Criar prontuário"}
            </Button>
          }
        >
          Este paciente ainda não possui prontuário.
        </Alert>
      )}

      {prontuario && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Chip label={`Paciente: ${patientName}`} />
            <Chip label={`Empresa: ${companyName}`} />
            <Chip
              color={getStatusColor(risk)}
              label={`Risco: ${riskLabels[risk as keyof typeof riskLabels] ?? formatEmpty(risk)}`}
            />
            <Chip
              color={getStatusColor(status)}
              label={`Status: ${prontuarioStatusLabels[status as keyof typeof prontuarioStatusLabels] ?? formatEmpty(status)}`}
            />
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dados gerais" />
            <Tab label="Medicamentos" />
            <Tab label="Anotações clínicas" />
            <Tab label="Histórico de atendimentos" />
          </Tabs>

          {!prontuarioId ? (
            <Alert severity="error">Não foi possível identificar o prontuário.</Alert>
          ) : (
            <>
              <TabPanel activeTab={activeTab} index={0}>
                <ProntuarioGeneralForm
                  prontuario={prontuario}
                  onUpdated={(updatedProntuario: Prontuario) =>
                    setApiData((prev) =>
                      prev ? { ...prev, prontuario: updatedProntuario } : prev
                    )
                  }
                  onNotify={showToast}
                />
              </TabPanel>
              <TabPanel activeTab={activeTab} index={1}>
                <ProntuarioMedications
                  prontuarioId={prontuarioId}
                  onNotify={showToast}
                />
              </TabPanel>
              <TabPanel activeTab={activeTab} index={2}>
                <ClinicalNotesList
                  prontuarioId={prontuarioId}
                  idColaborador={idColaborador}
                  onNotify={showToast}
                />
              </TabPanel>
              <TabPanel activeTab={activeTab} index={3}>
                <ClinicalHistory
                  idColaborador={idColaborador}
                  initialHistory={apiData?.historico_atendimentos}
                  onNotify={showToast}
                />
              </TabPanel>
            </>
          )}
        </>
      )}
    </Box>
  );

  return (
    <>
      {isDialog ? (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogContent sx={{ p: 2 }}>{content}</DialogContent>
        </Dialog>
      ) : (
        <Drawer anchor="right" open={open} onClose={onClose}>
          {content}
        </Drawer>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProntuarioDrawer;
